'use client'

import { Icon } from '@components/primitives/Icon'
import Link from '@components/primitives/Link'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { InterlinkItem } from '@modules/trials/search/interlink/getInterlinks'
import { useState } from 'react'

export default function RelatedLinksCardSection({
  gridCols,
  icon,
  links,
  linksToShow,
  noFollow,
  subtitle,
  title,
}: {
  gridCols?: 'grid-cols-4' | 'grid-cols-2'
  icon?: IconDefinition
  links: InterlinkItem[]
  linksToShow: number
  noFollow?: boolean
  subtitle?: string
  title?: string
}) {
  const [showAll, setShowAll] = useState(false)
  const hasMoreItems = links.length > linksToShow
  const visibleLinks = showAll ? links : links.slice(0, linksToShow)

  return (
    <section className='w-full'>
      {title && (
        <h3
          className={classMerge(
            'pb-4 text-lg font-extrabold leading-normal tracking-[-0.015em] text-neutral900 antialiased md:leading-normal lg:text-2xl',
          )}
        >
          {title}
        </h3>
      )}
      <div className='w-full rounded-lg border-[1.5px] border-neutral100 bg-white p-6'>
        {subtitle && (
          <Text
            className='pb-4 text-neutral700'
            element='span'
            styleName='p-tiny-strong'
          >
            {icon && (
              <Icon className='mr-1 text-primary500' icon={icon} size='sm' />
            )}
            {subtitle}
          </Text>
        )}
        <div
          className={classMerge(
            'grid grid-cols-1 gap-2 lg:grid-cols-4 lg:gap-2',
            `lg:${gridCols}`,
          )}
        >
          {visibleLinks.map((link, index) => (
            <div className='flex items-center justify-center gap-2' key={index}>
              <Link
                className='w-full overflow-hidden'
                href={link.url ?? link.link}
                onClick={() => {
                  trackEvent(TrackingEvents.SEARCH_LANDER_INTERLINK_CLICKED, {
                    val: link.title,
                  })
                }}
                prefetch={false}
                rel={noFollow ? 'nofollow' : undefined}
              >
                <Text
                  className={classMerge(
                    'truncate text-neutral800 underline decoration-primary200/50 underline-offset-2 antialiased',
                  )}
                  element='p'
                  styleName='p-3xtiny-strong'
                >
                  {link.title}
                </Text>
              </Link>
            </div>
          ))}
        </div>

        {hasMoreItems && (
          <div className='mt-2 flex justify-start lg:hidden'>
            <button
              className='focus:outline-none'
              onClick={() => setShowAll(!showAll)}
            >
              <Text
                className='text-primary500 antialiased'
                styleName='p-tiny-strong'
              >
                {showAll ? 'See Less' : 'See More'}
              </Text>
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
